/**
 * TransactionList (FE-2). Renders the filtered, offset/limit-paged transaction
 * rows with a full-set summary (count + signed sum from `meta`), infinite-scroll
 * loading, and loading / empty / error states.
 */
import { Receipt } from 'lucide-react';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { EmptyState } from '../../components/ui/EmptyState.tsx';
import { ErrorState } from '../../components/ui/ErrorState.tsx';
import { SkeletonList } from '../../components/ui/Skeleton.tsx';
import { StaleDataNotice } from '../../components/ui/StaleDataNotice.tsx';
import { useBucketMap } from '../../lib/hooks/useBuckets.ts';
import { useInfiniteScroll } from '../../lib/hooks/useInfiniteScroll.ts';
import type { TransactionsListResult } from '../../lib/hooks/useTransactions.ts';
import { TransactionRow } from './TransactionRow.tsx';

export interface TransactionListProps {
  list: TransactionsListResult;
}

export function TransactionList({ list }: TransactionListProps) {
  const bucketMap = useBucketMap();
  const {
    items,
    total,
    sumAmount,
    isLoading,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = list;

  const sentinelRef = useInfiniteScroll<HTMLDivElement>({
    hasMore: hasNextPage,
    isLoading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
  });

  // Data-first (§5.6): a failed background refetch must not replace the still
  // cached in-memory rows with a full-page error — that is reserved for
  // error-with-no-data. With rows present the failure shows as an inline notice.
  if (isError && items.length === 0) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (isLoading) {
    return (
      <div className="px-4 py-4 sm:px-6">
        <SkeletonList rows={8} />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        icon={Receipt}
        title="Keine Transaktionen"
        description="Für die aktuellen Filter wurden keine Transaktionen gefunden."
      />
    );
  }

  return (
    <div>
      {isError ? (
        <div className="px-4 pt-3 sm:px-6">
          <StaleDataNotice onRetry={refetch} />
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5 text-xs font-medium text-slate-500 sm:px-6 dark:border-slate-800/70 dark:text-slate-400">
        <span className="tabular-nums">
          {total} {total === 1 ? 'Transaktion' : 'Transaktionen'}
        </span>
        <span className="flex items-center gap-1.5">
          Summe
          <AmountText value={sumAmount} showPlus />
        </span>
      </div>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800/70">
        {items.map((txn) => (
          <li key={txn.id}>
            <TransactionRow txn={txn} bucketMap={bucketMap} />
          </li>
        ))}
      </ul>

      <div ref={sentinelRef} className="h-1" aria-hidden />

      {isFetchingNextPage ? (
        <div className="flex items-center justify-center gap-2 py-4 text-sm text-slate-500 dark:text-slate-400">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-200 border-t-sky-600 dark:border-slate-700 dark:border-t-sky-500" />
          Lade weitere …
        </div>
      ) : null}

      {!hasNextPage && items.length > 0 ? (
        <p className="py-4 text-center text-xs tabular-nums text-slate-400 dark:text-slate-500">
          Alle {total} Transaktionen geladen
        </p>
      ) : null}
    </div>
  );
}
